import { useCallback, useEffect, useState, useTransition, useMemo, useRef } from 'react';
import debounce from 'lodash.debounce';
import { ContentContainer } from '../../../../../components/Filters/components/ContentContainer';
import { ControlsPanel } from '../../../../../components/Filters/components/ControlsPanel';
import { CheckboxList } from '../../../../../components/Filters/components/CheckboxList';
import { SearchInput } from '../../../../../components/Filters/components/SearchInput';
import { ItemBadgesContainer } from '../../../../../components/Filters/components/ItemBadgesContainer';
import { FilterLoader } from '../../../../../components/Filters/components/FilterLoader';
import { NoResults } from '../../../../../components/Filters/components/NoResults';
import { API, ShareAPI } from '../../../../../API';
import { useTranslation } from 'react-i18next';
import { useBreakdownsFilterContext } from '../BreakdownsFilterContext';

export const CountriesChooser = () => {
  const { t } = useTranslation();
  const { state, isShare, updateDraftFilters } = useBreakdownsFilterContext();
  const api = isShare ? ShareAPI : API;
  const [countries, setCountries] = useState([]);
  const [defaultOptions, setDefaultOptions] = useState([]);
  const [allLoadedCountries, setAllLoadedCountries] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [isPending, startTransition] = useTransition();

  const selectedCountries = state.draftFilters.countries.value.map(c => parseInt(c));

  const getAllCountries = useCallback(() => {
    startTransition(() => {
      api.fetch('GET', '/API/v1/countries')
        .then((data) => {
          const optionsList = data.objects.slice(0, 10).map((country) => ({
            value: country.id,
            label: country.name,
          }));
          setDefaultOptions(optionsList);
          setAllLoadedCountries(prev => {
            const existingIds = new Set(prev.map(c => c.value));
            const newCountries = optionsList.filter(c => !existingIds.has(c.value));
            return [...prev, ...newCountries];
          });
        })
        .catch((error) => console.error('Failed to fetch countries:', error));
    });
  }, [api]);

  useEffect(getAllCountries, [getAllCountries]);

  useEffect(() => {
    if (selectedCountries.length === 0) return;
    const missingIds = selectedCountries.filter(id => !allLoadedCountries.some(c => c.value === id));
    if (missingIds.length === 0) return;

    missingIds.forEach(countryId => {
      api.fetch('GET', `/API/v1/countries/${countryId}`)
        .then((country) => {
          setAllLoadedCountries(prev => {
            if (prev.some(c => c.value === country.id)) return prev;
            return [...prev, { value: country.id, label: country.name }];
          });
        })
        .catch((error) => console.error(`Failed to fetch country ${countryId}:`, error));
    });
  }, [selectedCountries.length, api]);

  const getCountries = useCallback((inputValue) => {
    if (!inputValue) {
      setCountries([]);
      return;
    }
    startTransition(() => {
      api.fetch('GET', `/API/v1/countries?q=${inputValue}`)
        .then((data) => {
          const optionsList = data.objects.map((country) => ({
            value: country.id,
            label: country.name,
          }));
          setCountries(optionsList);
          setAllLoadedCountries(prev => {
            const existingIds = new Set(prev.map(c => c.value));
            const newCountries = optionsList.filter(c => !existingIds.has(c.value));
            return [...prev, ...newCountries];
          });
        })
        .catch((error) => console.error('Failed to fetch countries:', error));
    });
  }, [api]);

  const debouncedSearchRef = useRef(debounce((value) => getCountries(value), 500));

  useEffect(() => {
    debouncedSearchRef.current = debounce((value) => getCountries(value), 500);
  }, [getCountries]);

  useEffect(() => {
    const debouncedSearch = debouncedSearchRef.current;
    return () => debouncedSearch.cancel();
  }, []);

  const handleSearchChange = (value) => {
    setSearchValue(value);
    debouncedSearchRef.current(value);
  };

  const handleClearAll = () => {
    updateDraftFilters({ countries: { value: [], exclude: false } });
  };

  const handleToggleCountry = useCallback((countryId) => {
    const currentCountries = state.draftFilters.countries.value;
    const countryIdStr = String(countryId);

    if (currentCountries.includes(countryIdStr)) {
      updateDraftFilters({
        countries: {
          value: currentCountries.filter(id => id !== countryIdStr),
          exclude: state.draftFilters.countries.exclude,
        },
      });
    } else {
      updateDraftFilters({
        countries: {
          value: [...currentCountries, countryIdStr],
          exclude: state.draftFilters.countries.exclude,
        },
      });
    }
  }, [state.draftFilters.countries, updateDraftFilters]);

  const listToShow = countries.length > 0 ? countries : defaultOptions;
  const isNoResults = !isPending && searchValue && countries.length === 0;

  const checkboxItems = useMemo(() => {
    return listToShow.map((country) => ({
      name: country.label,
      checked: selectedCountries.includes(typeof country.value === 'string' ? parseInt(country.value) : country.value),
      onChange: () => handleToggleCountry(country.value),
      disabled: false,
      icon: null,
    }));
  }, [listToShow, selectedCountries, handleToggleCountry]);

  const badges = useMemo(() => {
    return selectedCountries.map(countryId => {
      const country = allLoadedCountries.find(c => c.value === countryId);
      return {
        name: country ? country.label : String(countryId),
        onChange: () => handleToggleCountry(countryId),
      };
    });
  }, [selectedCountries, allLoadedCountries, handleToggleCountry]);

  return (
    <ContentContainer
      searchComponent={
        <SearchInput
          value={searchValue}
          onChange={handleSearchChange}
          placeholder={t('Search countries')}
          hint={t('Use search to find options not shown in the list')}
          id="breakdown-country-search"
        />
      }
      actionsComponent={
        <ControlsPanel
          id="breakdown-country-filter"
          count={selectedCountries.length}
          isExclude={state.draftFilters.countries.exclude}
          showExcludeSwitch={true}
          showSelectAll={false}
          onClearAll={handleClearAll}
          onExcludeClick={() => {
            updateDraftFilters({
              countries: {
                value: state.draftFilters.countries.value,
                exclude: !state.draftFilters.countries.exclude,
              },
            });
          }}
        />
      }
      badgesComponent={selectedCountries.length > 0 ? <ItemBadgesContainer badges={badges} /> : undefined}
    >
      {isPending ? <FilterLoader /> : isNoResults ? <NoResults /> : <CheckboxList items={checkboxItems} />}
    </ContentContainer>
  );
};
